import { useSearch } from "@tanstack/react-router";
import { Button, Tag } from "antd";
import { useGetPartnersQuery } from "api/Partner/hooks";
import { InvitePartnerDrawer } from "components/Drawers/InvitePartnerDrawer/InvitePartnerDrawer";
import { PartnerDetailDrawer } from "components/Drawers/PartnerDetailDrawer/PartnerDetailDrawer";
import { useTenantCategorySelectOptions } from "components/UI/Selects/TenantCategorySelectSearchable/useTenantCategorySelectOptions.ts";
import { ResponseWithPagination, Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { useUser } from "hooks/useUser";
import { useRecoilValue } from "recoil";
import { UserWebTypeEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import PartnerData = App.Data.PartnerData;
import GetPartnersResponse = App.Http.Responses.Partner.GetPartnersResponse;

interface SearchParams {
    ["partnerDetail-partnerId"]?: string;
    ["invitePartner-partnerId"]?: string;
}

export const PartnersCatalogTable = () => {
    const tableId = TableId.PartnersCatalogTable;
    const { t } = usePaldockTranslation("tables.partners-catalog");
    const { t: tGeneral } = usePaldockTranslation("general.tenant_category");
    const { t: tWebType } = usePaldockTranslation("general.tenant_web_type");

    const drawerUtils = useDrawer();
    const searchParams = useSearch({ strict: false }) as SearchParams & Record<string, unknown>;
    const partnerDetailPartnerId = searchParams["partnerDetail-partnerId"];
    const invitePartnerPartnerId = searchParams["invitePartner-partnerId"];

    const tableAtomInstance = tableAtom<PartnerData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const { getAuthenticatedUser } = useUser();
    const currentUserId = getAuthenticatedUser()?.id;

    const { options: categoryFilterOptions } = useTenantCategorySelectOptions();

    const webTypeFilterOptions = Object.values(UserWebTypeEnum).map((value) => ({
        label: tWebType(value),
        value: value,
    }));

    const { getRowHandlers, getRowClassName } = useRowHandlers<PartnerData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.PartnerDetail,
                drawerParam: "partnerDetail-partnerId",
            },
        ],
    });

    const columnConfig: TableColumnType<PartnerData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.name")} />,
            dataIndex: "nickname",
            key: "nickname",
            sorter: true,
            ellipsis: true,
            width: 200,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.category")} />,
            dataIndex: "catalogCategories",
            key: "catalogCategories.category",
            render: (_, record) => {
                const categories = record.catalogCategories ?? [];
                const extraCount = Math.max(categories.length - 1, 0);

                if (!categories[0]) {
                    return <EmptyCell />;
                }

                return (
                    <div className="flex items-center gap-2">
                        <Tag>{tGeneral(categories[0].category)}</Tag>
                        {extraCount > 0 && <Tag className="m-0">+{extraCount}</Tag>}
                    </div>
                );
            },
            ellipsis: false,
            sorter: true,
            width: 250,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "catalogCategories.category",
            filterOptions: categoryFilterOptions,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.type")} />,
            dataIndex: "catalogWebTypes",
            key: "catalogWebTypes.web_type",
            render: (_, record) => {
                const webTypes = record.catalogWebTypes ?? [];
                const extraCount = Math.max(webTypes.length - 1, 0);

                if (!webTypes[0]) {
                    return <EmptyCell />;
                }

                return (
                    <div className="flex items-center gap-2">
                        <Tag>{tWebType(webTypes[0].web_type)}</Tag>
                        {extraCount > 0 && <Tag className="m-0">+{extraCount}</Tag>}
                    </div>
                );
            },
            ellipsis: false,
            sorter: true,
            width: 200,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "catalogWebTypes.web_type",
            filterOptions: webTypeFilterOptions,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.websites")} />,
            dataIndex: "websites",
            key: "websites",
            render: (_, record) => (
                <div className="flex flex-col gap-1">
                    {record.websites?.map((ws) => {
                        let hostname: string;
                        try {
                            hostname = new URL(ws.url).hostname;
                        } catch {
                            hostname = ws.url;
                        }
                        return (
                            <a
                                key={ws.id}
                                href={ws.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {hostname}
                            </a>
                        );
                    })}
                </div>
            ),
            ellipsis: false,
            width: 200,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("actions.invite")} />,
            dataIndex: "id",
            key: "actions",
            width: 120,
            render: (_, record) => {
                if (record.id === currentUserId) {
                    return "-";
                }

                return (
                    <Button
                        type="primary"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            drawerUtils.show(DrawerId.InvitePartner, { "invitePartner-partnerId": record.id });
                        }}
                    >
                        {t("actions.invite")}
                    </Button>
                );
            },
        },
    ];

    const fetchPartnersQuery = useGetPartnersQuery(
        {
            page: {
                number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
                size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
            },
            filter: {
                filter: tableState.filter?.filter ?? {},
                include: "catalogCategories,websites,catalogWebTypes",
                sort: tableState.filter?.sort ?? "nickname",
            },
        },
        { enabled: false }
    );

    const tableFetchDataUtils = useTableDataApiOperations<GetPartnersResponse, void, PartnerData>(
        tableId,
        fetchPartnersQuery
    );

    return (
        <>
            <Table<ResponseWithPagination, PartnerData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetPartnersResponse}
                dataSource={tableFetchDataUtils.tableData?.partners || []}
                rowKey={(record) => record.id}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                enableFilters={true}
                loading={tableFetchDataUtils.fetchQuery.isFetching}
                autoScrollWidth={true}
                onRow={getRowHandlers}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
            />
            {partnerDetailPartnerId && <PartnerDetailDrawer partnerId={partnerDetailPartnerId} />}
            {invitePartnerPartnerId && <InvitePartnerDrawer partnerId={invitePartnerPartnerId} />}
        </>
    );
};
